import type { EditorialTile } from './editorialTile';

const MIN_TILES = 3;
export const MAX_TILES = 6;

// A previewed block that cannot be shown, with the reason, so the author sees
// the problem in place of the tile.
export type EditorialTileError = { id: string; error: string };

export type EditorialTileSlot = EditorialTile | EditorialTileError;

export const isEditorialTileError = (
  slot: EditorialTileSlot
): slot is EditorialTileError => 'error' in slot;

// A live row is all Workbench tiles or none; with too few, the page shows its
// own fallback tiles instead of a short row. A preview row shows exactly the
// requested blocks.
export type EditorialTileRow =
  | { mode: 'live'; tiles: EditorialTile[] }
  | { mode: 'fallback' }
  | { mode: 'preview'; slots: EditorialTileSlot[] };

export type PositionedTile = {
  tile: EditorialTile;
  position?: number | undefined;
};

export type PositionedSlot = {
  slot: EditorialTileSlot;
  position?: number | undefined;
};

// A Workbench id requested for preview; invalid ids are never sent to WCMS.
export type EditorialPreviewId = { id: string; isValid: boolean };

export const FALLBACK_ROW: EditorialTileRow = { mode: 'fallback' };

const rank = ({ position }: { position?: number | undefined }): number =>
  position ?? Number.MAX_SAFE_INTEGER;

// Unpositioned entries go last. The sort is stable, so equal ranks keep the
// order they arrived in.
const byPosition = <T extends { position?: number | undefined }>(
  entries: T[]
): T[] => [...entries].sort((a, b) => rank(a) - rank(b));

export const getEditorialTileRow = (
  candidates: PositionedTile[]
): EditorialTileRow => {
  const tiles = byPosition(candidates)
    .slice(0, MAX_TILES)
    .map(({ tile }) => tile);

  return tiles.length < MIN_TILES ? FALLBACK_ROW : { mode: 'live', tiles };
};

export const getEditorialPreviewRow = (
  candidates: PositionedSlot[]
): EditorialTileRow => ({
  mode: 'preview',
  slots: byPosition(candidates).map(({ slot }) => slot),
});
